import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

const STORAGE_KEY = "wn.session.v1";
// Impersonation envelope — keeps the real admin user "originalUser"
// alongside the impersonated user so we can swap back without re-auth.
const IMP_KEY = "wn.imp.v1";

const RoleContext = createContext(null);

export function RoleProvider({ children }) {
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { role: "anonymous", user: null };
    } catch {
      return { role: "anonymous", user: null };
    }
  });
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = sessionStorage.getItem(IMP_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }, [session]);

  useEffect(() => {
    if (impersonation) {
      sessionStorage.setItem(IMP_KEY, JSON.stringify(impersonation));
    } else {
      sessionStorage.removeItem(IMP_KEY);
    }
  }, [impersonation]);

  const signIn = useCallback(async (role) => {
    const { data } = await api.post("/auth/sign-in", { role });
    setSession({ role: data.role, user: data.user || null });
    setImpersonation(null);
    return data;
  }, []);

  const signInAsUser = useCallback(async (userId) => {
    const { data } = await api.post(`/auth/sign-in-as/${encodeURIComponent(userId)}`);
    setSession({ role: data.role, user: data.user || null });
    setImpersonation(null);
    return data;
  }, []);

  const setSessionDirect = useCallback((role, user) => {
    setSession({ role, user: user || null });
  }, []);

  const refreshUser = useCallback(async () => {
    const id = impersonation?.target?.id || session.user?.id;
    if (!id) return null;
    try {
      const { data } = await api.get(`/me/${encodeURIComponent(id)}`);
      if (impersonation) {
        setImpersonation((imp) => imp ? { ...imp, target: data } : imp);
      } else {
        setSession((s) => ({ ...s, user: data }));
      }
      return data;
    } catch {
      return null;
    }
  }, [session.user?.id, impersonation]);

  const signOut = useCallback(() => {
    setSession({ role: "anonymous", user: null });
    setImpersonation(null);
  }, []);

  /**
   * Begin impersonation. `targetUser` and `targetRole` come from the
   * backend `/admin-portal/impersonate/start` response. The current
   * admin session is preserved in `originalUser` so we can swap back.
   */
  const beginImpersonation = useCallback((targetUser, reason) => {
    if (!targetUser || session.role !== "platform_admin") return;
    setImpersonation({
      originalUser: session.user,
      originalRole: session.role,
      target: targetUser,
      targetRole: targetUser.role,
      reason: reason || "",
      started_at: new Date().toISOString(),
    });
  }, [session]);

  const endImpersonation = useCallback(() => {
    setImpersonation(null);
  }, []);

  // Effective role/user blended from impersonation state.
  const effectiveRole = impersonation ? impersonation.targetRole : session.role;
  const effectiveUser = impersonation ? impersonation.target : session.user;

  const value = useMemo(
    () => ({
      role: effectiveRole,
      user: effectiveUser,
      isAnonymous: effectiveRole === "anonymous",
      // Impersonation surface:
      impersonation,
      isImpersonating: !!impersonation,
      originalRole: impersonation?.originalRole || null,
      originalUser: impersonation?.originalUser || null,
      beginImpersonation,
      endImpersonation,
      // Session controls:
      signIn,
      signInAsUser,
      setSessionDirect,
      refreshUser,
      signOut,
    }),
    [
      effectiveRole, effectiveUser, impersonation,
      beginImpersonation, endImpersonation,
      signIn, signInAsUser, setSessionDirect, refreshUser, signOut,
    ]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used inside <RoleProvider>");
  return ctx;
}
